<script lang="ts">
  /**
   * SetRibbon — a swim rendered as its sets. Per set: a chips-row (one rounded
   * chip per length, colored by stroke) + a label line ("The Rockalita · 200m").
   * Rest gaps between sets render as a thin rule with plain-words rest. The whole
   * set block is one 44px+ tap target that opens the full-screen SetDetailSheet.
   *
   * Reads dw.setGroups (the app-wide set definition). No BPM, no "SWOLF" — the
   * ribbon is orientation, not reading; the load-bearing text is the label line.
   *
   * Visual reference: /tmp/set-ribbon-sketch.html (app design tokens applied).
   */
  import type { DerivedWorkout } from "../lib/lapAnalysis";
  import type { SetGroup } from "../lib/setGroups";
  import { strokeColor } from "../lib/designTokens";
  import { openOverlay } from "../lib/overlay";
  import { fmtRest } from "../lib/format";
  import { haptic } from "../lib/haptics";
  import { SET_DISPLAY } from "../lib/setLibrary";
  import { tapGuard } from "../lib/actions/tapGuard";
  import { chipsToRender, keydownBelongsToNestedControl } from "./setRibbon.logic";

  // `showcase` (default false) is the PUBLIC-page flag. When true, each NAMED
  // signature set gets a one-line plain-English decode under its label so an
  // outsider knows what "Killer BS" / "Book-ended Rockalita" mean (5/5 blind
  // panel flagged the jargon; "BS" even read as profanity). The decode text is
  // the REAL setLibrary plainDefinition — reused verbatim, never invented.
  // Generic sets (Warmup, Mixed, Cooldown, Freestyle Set) get no decode. The app
  // is unchanged: production callers pass nothing → false → no decode rendered.
  interface Props {
    /** The swim whose `setGroups` the ribbon draws. The Club card passes `groups`
     *  instead (a club swim may carry no firehose), and may omit this. */
    dw?: DerivedWorkout | null;
    /** Sets to draw in place of `dw.setGroups` (the Club card, B-026 build 13). */
    groups?: SetGroup[] | null;
    /** false = a picture: no tap target, no chevron (a clubmate's swim on the Club
     *  card; SetDetailSheet writes through the Swim tab's selected swim). Default true. */
    interactive?: boolean;
    /** A set tap goes here instead of opening SetDetailSheet on the selected swim
     *  (the Club card opens the swim on the Swim tab). */
    onSetTap?: ((setIndex: number) => void) | null;
    showcase?: boolean;
    correctedLapIndices?: Set<number>;
    onLapTap?: ((lapIndex: number) => void) | null;
    /** Which corrected length's reveal is currently open — drives the tapped
     *  chip's selected state so a tap visibly does something (persona panel
     *  B2: an unstyled tap read as broken). */
    selectedLapIndex?: number | null;
    /** Corrections-card mode (persona panel B3/B5): show ONLY the sets that
     *  contain a fix, every length gets its own chip (no MAX_CHIPS cap, no
     *  hoisting — `chipsToRender` never runs), and bookend dimming is off so a
     *  fix can never render dimmer than an untouched chip. The normal ribbon
     *  (SetRibbonSection) never sets this. */
    correctionMode?: boolean;
  }
  let {
    dw = null,
    groups = null,
    interactive = true,
    onSetTap = null,
    showcase = false,
    correctedLapIndices = new Set<number>(),
    onLapTap = null,
    selectedLapIndex = null,
    correctionMode = false,
  }: Props = $props();

  const allGroups = $derived(groups ?? dw?.setGroups ?? []);
  const visibleGroups = $derived(
    correctionMode
      ? allGroups.filter((s) => s.laps.some((l) => correctedLapIndices.has(l.index)))
      : allGroups,
  );

  /** The plain-English decode for a NAMED signature set, or "" when the set is
   *  generic / unnamed (no setId, or a non-signature id). Pulled from the shared
   *  SET_DISPLAY map so the ribbon, the recognition card, and the ledger all read
   *  the same definition. Only consulted in showcase mode. */
  function decodeFor(setId: string | null): string {
    if (!setId) return "";
    const entry = SET_DISPLAY[setId];
    return entry && entry.isSignature ? entry.plainDefinition : "";
  }

  function open(setIndex: number) {
    haptic("selection");
    if (onSetTap) onSetTap(setIndex);
    else openOverlay({ kind: "set-detail", setIndex });
  }

  function onKey(e: KeyboardEvent, setIndex: number) {
    if (e.key !== "Enter" && e.key !== " ") return;
    // A corrected-length chip is a focusable <button> nested inside the
    // set-block. Svelte delegates this keydown to the root and walks
    // composedPath(), so a keydown while a chip is focused still reaches
    // here — bail and let the chip's own button synthesize its click,
    // otherwise this preventDefault() steals Enter/Space from it and the
    // whole-set sheet opens instead of the length reveal.
    if (keydownBelongsToNestedControl(e.target)) return;
    e.preventDefault();
    open(setIndex);
  }
</script>

<section class="ribbon" aria-label={showcase ? "My swim, as sets" : "Your swim, as sets"}>
  {#each visibleGroups as s (s.index)}
    {#if s.restBeforeMs !== null && fmtRest(s.restBeforeMs) !== ""}
      <div class="gap" aria-hidden="true">
        <span class="gap-line"></span>
        <span class="gap-rest">{fmtRest(s.restBeforeMs)}</span>
        <span class="gap-line"></span>
      </div>
    {/if}

    {#if interactive}
      <div
        class="set-block"
        class:bookend={!correctionMode && (s.setId === "warmup" || s.setId === "cooldown")}
        role="button"
        tabindex="0"
        aria-label={`${s.label}, ${Math.round(s.meters)} meters, ${s.laps.length} ${dw?.isOpenWater ? "splits" : "lengths"}. Tap to open.`}
        use:tapGuard={() => open(s.index - 1)}
        onkeydown={(e) => onKey(e, s.index - 1)}
      >
        {@render setBody(s)}
      </div>
    {:else}
      <div class="set-block still" class:bookend={s.setId === "warmup" || s.setId === "cooldown"}>
        {@render setBody(s)}
      </div>
    {/if}
  {/each}
</section>

{#snippet setBody(s: SetGroup)}
      {#if s.isLoneLength}
        <div class="chips-row">
          <span class="chip chip-lone" style:background={strokeColor(s.laps[0].strokeStyle)}></span>
        </div>
        <div class="set-label">
          <span class="lone-note">Lone {s.laps[0].strokeStyle} · {Math.round(s.meters)}m</span>
          <!-- P1: right-aligned tap chevron — reads the row as tappable. -->
          <span class="tap-chevron" aria-hidden="true">›</span>
        </div>
      {:else}
        {@const chips = correctionMode ? { shown: s.laps, overflow: 0 } : chipsToRender(s.laps, correctedLapIndices)}
        <div class="chips-row">
          {#each chips.shown as lap (lap.index)}
            {#if correctedLapIndices.has(lap.index) && onLapTap}
              <button
                type="button"
                class="chip chip-fixed"
                class:chip-fixed-excluded={lap.effectiveExcluded}
                class:selected={selectedLapIndex === lap.index}
                style:background={strokeColor(lap.strokeStyle)}
                aria-label={lap.effectiveExcluded
                  ? `Fixed length, ${lap.strokeStyle}. Doesn't count in your totals. Tap for details.`
                  : `Fixed length, ${lap.strokeStyle}. Tap for details.`}
                onclick={(e) => {
                  e.stopPropagation();
                  onLapTap(lap.index);
                }}
              >
                <span class="chip-tick" aria-hidden="true">✓</span>
              </button>
            {:else}
              <span class="chip" style:background={strokeColor(lap.strokeStyle)}></span>
            {/if}
          {/each}
          {#if chips.overflow > 0}
            <span class="chip chip-more">+{chips.overflow} more</span>
          {/if}
        </div>
        <div class="set-label">
          <span class="set-label-text"><strong>{s.label}</strong> · {Math.round(s.meters)}m</span>
          <!-- P1: right-aligned tap chevron — reads the row as tappable. -->
          <span class="tap-chevron" aria-hidden="true">›</span>
        </div>
        {#if showcase && decodeFor(s.setId) !== ""}
          <!-- showcase-only: plain-English decode of a NAMED signature set so an
               outsider knows what it means. Real setLibrary plainDefinition. -->
          <p class="set-decode">{decodeFor(s.setId)}</p>
        {/if}
      {/if}
{/snippet}

<style>
  .ribbon {
    display: flex;
    flex-direction: column;
  }
  .gap {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    margin: var(--space-2) 0;
  }
  .gap-line {
    flex: 1;
    height: 1px;
    background: var(--divider);
  }
  .gap-rest {
    font-size: 12px;
    color: var(--text-dim);
    font-weight: 500;
    letter-spacing: 0.2px;
    white-space: nowrap;
  }

  .set-block {
    /* One tap target per set; wet-hands minimum height. */
    min-height: 44px;
    padding: var(--space-2) 0;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    border-radius: var(--radius-button);
    transition: background-color var(--dur-micro) var(--ease-swim);
  }
  .set-block.bookend {
    opacity: 0.7;
  }
  /* A picture, not a control: no pressed state, no pointer, no chevron. */
  .set-block.still {
    cursor: default;
  }
  .set-block.still:active {
    background: transparent;
  }
  .set-block.still .tap-chevron {
    display: none;
  }
  .set-block:active {
    background: var(--bg-raised);
  }
  .set-block:focus-visible {
    outline: 2px solid var(--accent-primary);
    outline-offset: 2px;
  }

  .chips-row {
    display: flex;
    flex-wrap: wrap;
    gap: 3px;
    margin-bottom: 6px;
  }
  .chip {
    /* border-box so the 1px hairline does not change the chip footprint. */
    box-sizing: border-box;
    width: 13px;
    height: 22px;
    border-radius: 3px;
    flex-shrink: 0;
    /* Crisp hairline so every chip's geometry reads on the near-black canvas
       (2026-06 low-vision blind-review BLOCKER — replaces the failed inset shadow). */
    border: 1px solid rgba(255, 255, 255, 0.28);
  }
  .chip-fixed {
    position: relative;
    /* Wider than a plain 13px chip — a fixed length should visually stand out
       in correction mode, and the extra width grows the chip's OWN tap
       footprint instead of relying entirely on the invisible touch pad. */
    width: 18px;
    padding: 0;
    /* Double-ring halo, not just a brighter hairline: a white border PLUS a
       dark separation ring outside it, so the shape reads as different from
       a plain chip's 1px hairline even at 9px glyph scale, on any stroke
       colour, for a reader who can't rely on colour or the ✓ glyph alone
       (persona panel gate (d) — a single-channel signal is not enough). */
    border: 2px solid #fff;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.85);
    cursor: pointer;
    /* 18x22 is the visual chip; this pads the TOUCH target to iOS's 44px
       minimum height without changing the ribbon's layout. */
    outline-offset: 2px;
  }
  /* Excluded + fixed (review round 2, 2026-07-12): a length can be BOTH fixed
     AND not counted, and that combination needs its own mark — a colour
     change was ruled out (stroke colours are locked), so the ring itself goes
     from solid to dashed. Same footprint, same double-ring idea, one shape
     detail different. */
  .chip-fixed-excluded {
    border-style: dashed;
  }
  /* Tapped state (persona panel B2): a SHAPE change (grow + a second ring),
     not just a colour swap, so the tap visibly registers for a reader who
     can't rely on colour. */
  .chip-fixed.selected {
    transform: scale(1.2);
    box-shadow:
      0 0 0 1px rgba(0, 0, 0, 0.85),
      0 0 0 3px var(--accent-primary);
  }
  .chip-fixed::after {
    content: "";
    position: absolute;
    top: -11px;
    bottom: -11px;
    /* `.chips-row` gap is 3px. Reach is capped at 1px (well under half the
       gap) so two ADJACENT corrected chips' touch pads can never meet, let
       alone overlap — an ambiguous tap between them would open the wrong
       length, which is worse than a slightly smaller target. */
    left: -1px;
    right: -1px;
  }
  /* Louder tick (persona panel round 2, 2026-07-12): a reviewer miscounted 5
     checks as 4 at the old 9px. The ring + tick together are the loud signal
     here — colour is secondary — so both grew: bigger, bolder glyph, and a
     stronger separation ring above (0 0 0 1px -> 2px). */
  .chip-tick {
    font-size: 13px;
    font-weight: 700;
    line-height: 22px;
    color: #fff;
    text-shadow: 0 0 3px rgba(0, 0, 0, 0.95);
  }
  .chip-lone {
    width: 16px;
    height: 16px;
    border-radius: 4px;
    opacity: 0.6;
  }
  .chip-more {
    width: auto;
    min-width: 28px;
    padding: 0 6px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: var(--bg-raised);
    color: var(--text-muted);
    font-size: 11px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .set-label {
    /* Load-bearing readable text — the ribbon is the star, so the set names
       read at body size (15px), a clear step above the 12px rest labels. */
    font-size: 15px;
    color: var(--text-muted);
    font-weight: 500;
    /* Lay the label and the tap chevron on one row; chevron right-aligned. */
    display: flex;
    align-items: center;
    gap: var(--space-2);
  }
  .set-label-text {
    min-width: 0;
  }
  .set-label strong {
    color: var(--accent-primary);
    font-weight: 600;
  }
  .lone-note {
    font-size: 15px;
    color: var(--text-muted);
    font-style: normal;
  }
  /* showcase named-set decode — one plain-English line under a signature label.
     15px floor (Scott reads at a glance); readable muted, not dim, since it
     carries meaning for an outsider. */
  .set-decode {
    margin: 2px 0 0;
    font-size: 15px;
    line-height: 1.3;
    color: var(--text-muted);
  }
  /* P1: muted, >=17px chevron pushed to the right edge of the label row. */
  .tap-chevron {
    margin-left: auto;
    flex-shrink: 0;
    font-size: 17px;
    line-height: 1;
    color: var(--text-dim);
    font-weight: 400;
  }
</style>
